// packages/core/src/quizzes/content/goldenHandcuffs.ts
// ─────────────────────────────────────────────────────────────────────────
// "The Golden Handcuffs Index" (index engine, 0 to 100, high = more cuffed)
//
// Seven questions, max 10 points each. The score measures how expensive
// leaving would be across five dimensions: runway, lifestyle ratchet,
// unvested money, budget slack, and identity.
//
// Verified numbers used in copy (plain 4% arithmetic):
//   $1,000/mo of fixed cost forever → $1,000 × 12 ÷ 0.04 = $300,000
// ─────────────────────────────────────────────────────────────────────────

import type { IndexQuizContent } from '../DiscoveryTypes';

export const GOLDEN_HANDCUFFS: IndexQuizContent = {
	meta: {
		id: 'golden-handcuffs',
		title: 'The Golden Handcuffs Index',
		hook: 'You earn well. But when do you walk away?',
		engine: 'index',
		minutes: 3,
		status: 'live',
	},

	questions: [
		{
			type: 'index',
			id: 'gh-runway',
			prompt: 'If you quit today with nothing lined up, your savings would cover:',
			options: [
				{ label: 'Under 3 months', points: 10 },
				{ label: '3 to 6 months', points: 7 },
				{ label: '6 to 12 months', points: 4 },
				{ label: 'More than a year', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'gh-ratchet',
			prompt: 'Compare your monthly spending to five years ago:',
			options: [
				{ label: 'Roughly doubled, or more', points: 10 },
				{ label: 'Grown faster than my income', points: 7 },
				{ label: 'Grown about in step with my income', points: 4 },
				{ label: 'Flat or lower', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'gh-unvested',
			prompt: 'Unvested money you would walk away from (equity, bonuses, pension years):',
			options: [
				{ label: 'More than a year of pay', points: 10 },
				{ label: 'A few months of pay', points: 6 },
				{ label: 'A little', points: 3 },
				{ label: 'None', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'gh-paycut',
			prompt: 'Could you take a 30% pay cut for work you actually wanted?',
			options: [
				{ label: 'Impossible. The budget has no slack.', points: 10 },
				{ label: 'Painful. It would take big cuts.', points: 7 },
				{ label: 'Doable with some adjustments.', points: 3 },
				{ label: 'Easily.', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'gh-identity',
			prompt: 'How much of your identity is the job title?',
			options: [
				{ label: 'It is how I introduce myself.', points: 10 },
				{ label: 'A big part.', points: 6 },
				{ label: 'Some.', points: 3 },
				{ label: 'Barely any.', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'gh-priced',
			prompt: 'The last time you seriously priced out leaving:',
			options: [
				{ label: 'Never. It is not a thinkable option.', points: 10 },
				{ label: 'Years ago.', points: 6 },
				{ label: 'Sometime this year.', points: 3 },
				{ label: 'I have a written number and a date.', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'gh-fixed',
			prompt: 'Your fixed costs (housing, cars, schools, the subscriptions you would fight to keep) take up:',
			options: [
				{ label: 'Nearly all of my pay', points: 10 },
				{ label: 'Most of it', points: 7 },
				{ label: 'About half', points: 4 },
				{ label: 'Well under half', points: 0 },
			],
		},
	],

	bands: [
		{
			min: 75,
			label: 'Bolted in',
			insight:
				'The pay is real and so is the lock. Your lifestyle, runway, and identity are all wired into the job, which means the job owns the veto on every other version of your life. The trade you are making is high income now for zero optionality now. That can be a fine trade, but only when it is made on purpose, with a number and a date attached.',
		},
		{
			min: 50,
			label: 'Comfortably caught',
			insight:
				'You could leave, but it would hurt more than it should. The usual culprit is fixed costs: at a safe 4% yearly spend, every $1,000 of monthly fixed cost needs about $300,000 invested before it funds itself. Each recurring commitment you sign is another link, and most of them were never priced that way.',
		},
		{
			min: 25,
			label: 'Loosely held',
			insight:
				'The cuffs exist but the key is within reach. You have slack in the budget and some runway banked, which means the job is a choice you keep re-making rather than a wall. What is usually missing at this score is the actual number: the date when staying becomes fully optional.',
		},
		{
			min: 0,
			label: 'Free hands',
			insight:
				'The job does not own you. Low fixed costs, real runway, and an identity that is not the title. The interesting question left is timing: with this much slack, the walk-away date may be closer than you have bothered to check.',
		},
	],

	cta: {
		label: 'See your walk-away date',
		to: '/simulate',
		blurb: 'Handcuffs open on a date, not a feeling. Nisk turns your pay, spending, and savings into the actual date, and shows which link in the chain moves it most.',
	},

	shareText:
		'My Golden Handcuffs Index is {score}/100 on Nisk. Every $1,000 of monthly fixed cost takes about $300,000 invested to walk away from. Find your number: https://nisk.co/quizzes/golden-handcuffs',
};
